import { useCallback, useMemo } from 'react'
import { ParamKeyValuePair, useSearchParams } from 'react-router-dom'
import { Paging } from '../components/table/paging-hook'

const DEFAULT_SIZE = '50'

export function mapObjectValues(obj: object, mapFunction: (value: any) => any) {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v)
      .map(([key, value]) => [key, mapFunction(value)])
  )
}

const SPLIT_REGEX = /[\s,;\t]+/

export function textInputToSearchParams(input: Record<string, string>) {
  return Object.entries(input).reduce((obj, [key, value]) => {
    return {
      ...obj,
      [key]: value?.toString().trim().length
        ? key === 'shipToNames'
          ? value.toString()
          : value.toString().trim().split(SPLIT_REGEX).filter(Boolean)
        : undefined,
    }
  }, {})
}

export interface QueryParams {
  paging?: {
    from?: number
    size?: number
  }
  searchParameters?: Record<string, string[]>
}

export interface QueryParamsReturnType extends QueryParams {
  paging?: Paging
}

export function useQueryParameters(): [QueryParamsReturnType, (qp: QueryParams) => void] {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams()

  const {
    from: fromStr = '0',
    size: sizeStr = DEFAULT_SIZE,
    ...searchParametersFromUrl
  } = useMemo(() => Object.fromEntries(urlSearchParams), [urlSearchParams])

  const updateQueryParameters = useCallback(
    ({ paging, searchParameters }: QueryParams) => {
      const keyValueParameters = searchParameters
        ? Object.entries(searchParameters)
            .filter(([_, v]) => v)
            .map(([_, value]) => {
              return typeof value === 'string' ? [_, value] : [_, value.join(',')]
            })
        : Object.entries(searchParametersFromUrl)

      keyValueParameters.push(['from', paging?.from?.toString() ?? fromStr])
      keyValueParameters.push(['size', paging?.size?.toString() ?? sizeStr])
      setUrlSearchParams(keyValueParameters as ParamKeyValuePair[])
    },
    [fromStr, sizeStr, searchParametersFromUrl, setUrlSearchParams]
  )

  const setFrom = useCallback(
    (from: number) => {
      updateQueryParameters({ paging: { from } })
    },
    [updateQueryParameters]
  )

  const changeSize = useCallback(
    (newPageSize) => {
      updateQueryParameters({ paging: { from: 0, size: newPageSize } })
    },
    [updateQueryParameters]
  )

  const transformedParams: QueryParamsReturnType = useMemo(
    () => ({
      paging: {
        from: parseInt(fromStr),
        size: parseInt(sizeStr),
        changeSize,
        setFrom,
      },
      searchParameters: mapObjectValues(searchParametersFromUrl, (val: string) => val.split(',')),
    }),
    [fromStr, sizeStr, changeSize, setFrom, searchParametersFromUrl]
  )

  return [transformedParams, updateQueryParameters]
}
